import { get, isArray, each } from 'lodash'
import Store from '@/store/store'
import { DynamicFormField } from '../../DynamicFormTypes'

export function useSelectFormatting() {
  const formatSelectValue = (model: Record<string, any>, field: DynamicFormField): string => {
    if (!('items' in field) || !field.items) return ''
    if (!field.model) return ''

    const items = typeof field.items === 'function' ? field.items(model, field) : field.items
    if (!isArray(items)) return ''

    let value = ''
    const fieldValue = get(model, field.model)

    // Determine itemValue and itemText keys
    const itemValueKey = 'itemValue' in field && field.itemValue ? field.itemValue : 'value'
    const itemTextKey = 'itemText' in field && field.itemText ? field.itemText : 'text'

    items.forEach(item => {
      if ('multiple' in field && field.multiple && isArray(fieldValue)) {
        fieldValue.forEach(selectedValue => {
          if (typeof item === 'object') {
            if (selectedValue === get(item, itemValueKey)) {
              value += (value ? ', ' : '') + get(item, itemTextKey)
            }
          } else if (typeof item === 'string') {
            if (selectedValue === item) {
              value += (value ? ', ' : '') + item
            }
          }
        })
      } else {
        if (typeof item === 'object') {
          if (fieldValue === get(item, itemValueKey)) {
            value = get(item, itemTextKey)
          }
        } else if (typeof item === 'string' && fieldValue === item) {
          value = item
        }
      }
    })

    // Replace pipe separators for checkbox groups
    if (value && (('multiple' in field && field.multiple) || field.component === 'dynamic-checkbox-group')) {
      value = value.replace(/\|/g, ', ')
    }

    return value
  }

  const formatDataSourceValue = (model: Record<string, any>, field: DynamicFormField): string => {
    if (!('dataSource' in field) || !field.dataSource) return ''
    if (!field.model) return ''

    const { state, modelName } = field.dataSource
    const dataSource = Store.state[state || 'dynamicModule'][modelName]

    let value = ''
    const fieldValue = get(model, field.model)

    if (isArray(dataSource)) {
      each(dataSource, item => {
        const itemValue = 'itemValue' in field ? field.itemValue : 'id'
        const itemText = 'itemText' in field ? field.itemText : 'text'

        if (get(item, itemValue) === fieldValue) {
          value = get(item, itemText)
          return false // break the loop
        }
      })
    } else if (dataSource && 'itemText' in field) {
      value = dataSource[field.itemText || 'text']
    }

    return value
  }

  const getSelectedItem = (model: Record<string, any>, field: DynamicFormField): Record<string, any> => {
    if (!model || !field) return {}

    // User system type
    if (field.systemType === 'user' || field.systemType === 'users') {
      if (field.userModel) {
        return get(model, field.userModel)
      }
      return model
    }

    // Items-based selection
    if ('items' in field && field.items) {
      const items = typeof field.items === 'function' ? field.items(model, field) : field.items
      if (!isArray(items)) return {}
      if (!field.model) return {}

      const fieldValue = get(model, field.model)
      let selectedItem = {}

      const itemValueKey = 'itemValue' in field && field.itemValue ? field.itemValue : 'value'

      items.forEach(item => {
        if (typeof item === 'object') {
          if (fieldValue === get(item, itemValueKey)) {
            selectedItem = item
          }
        }
      })

      return selectedItem
    }

    // DataSource-based selection
    if ('dataSource' in field && field.dataSource) {
      const { state, modelName } = field.dataSource
      const dataSource = Store.state[state || 'dynamicModule'][modelName]

      if (isArray(dataSource)) {
        if (!field.model) return {}
        const fieldValue = get(model, field.model)
        let selectedItem = {}

        each(dataSource, item => {
          const itemValue = 'itemValue' in field ? field.itemValue : 'id'
          if (get(item, itemValue) === fieldValue) {
            selectedItem = item
            return false // break the loop
          }
        })

        return selectedItem
      } else if (dataSource) {
        return dataSource
      }
    }

    return {}
  }

  return {
    formatSelectValue,
    formatDataSourceValue,
    getSelectedItem
  }
}
